import React, { useState } from "react";
import {
    useGetGameBonusSummaryQuery,
    useGetGameBonusPayoutsQuery,
    useCreateGameBonusPayoutMutation,
    useGetGameBonusGlobalStatsQuery,
} from "../api";
import { Plus, Wallet, Search, Filter } from "lucide-react";
import { UserViewModal } from "../components/UserViewModal";

type Tab = "history" | "payouts";

const GameBonusPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>("history");
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);

    // Filters state
    const [filters, setFilters] = useState<{
        page: number;
        per_page: number;
        tournament_id?: string;
        date_from?: string;
        date_to?: string;
    }>({
        page: 1,
        per_page: 20,
    });

    const [filterDraft, setFilterDraft] = useState({ tournament_id: "", date_from: "", date_to: "" });

    const [payoutFilters, setPayoutFilters] = useState({
        page: 1,
        per_page: 20,
        fund_type: "",
    });

    // Global Stats query
    const { data: globalStatsResponse, isLoading: summaryLoading } = useGetGameBonusGlobalStatsQuery();
    const globalStats = globalStatsResponse?.data;

    // History tab (now showing list of funds/tournaments)
    const { data: fundsData, isLoading: historyLoading } = useGetGameBonusSummaryQuery(filters);

    // Payouts tab
    const { data: payoutsData, isLoading: payoutsLoading } = useGetGameBonusPayoutsQuery(payoutFilters, {
        skip: activeTab !== "payouts",
    });

    // Create payout mutation
    const [createPayout, { isLoading: isCreatingPayout }] = useCreateGameBonusPayoutMutation();
    const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
    const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
    const [payoutForm, setPayoutForm] = useState({
        fund_type: "tournament",
        amount: "",
        destination: "",
        comment: "",
    });

    const handleCreatePayout = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!payoutForm.amount || !payoutForm.fund_type) return;

        try {
            if (!selectedFundId) {
                alert("Выберите турнир для выплаты.");
                return;
            }

            await createPayout({
                game_bonus_fund_id: selectedFundId,
                fund_type: payoutForm.fund_type,
                amount: parseFloat(payoutForm.amount),
                destination: payoutForm.destination,
                comment: payoutForm.comment,
            }).unwrap();

            setIsPayoutModalOpen(false);
            setSelectedFundId(null);
            setPayoutForm({ fund_type: "tournament", amount: "", destination: "", comment: "" });
            alert("Выплата успешно добавлена");
        } catch (err: any) {
            alert(err?.data?.message || "Ошибка при создании выплаты");
        }
    };

    const handleUserClick = (userId: number) => {
        setSelectedUserId(userId);
        setIsUserModalOpen(true);
    };

    const applyFilters = () => {
        setFilters((prev) => ({
            ...prev,
            page: 1,
            tournament_id: filterDraft.tournament_id || undefined,
            date_from: filterDraft.date_from || undefined,
            date_to: filterDraft.date_to || undefined,
        }));
    };

    const resetFilters = () => {
        setFilterDraft({ tournament_id: "", date_from: "", date_to: "" });
        setFilters({ page: 1, per_page: 20 });
    };

    const formatAmount = (v: number | string) =>
        Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDate = (d: string) => new Date(d).toLocaleString("uk-UA");

    const renderPagination = (data: any, currentFilters: any, setFiltersFn: any) => {
        if (!data || data.meta.last_page <= 1) return null;

        return (
            <div className="flex justify-between items-center px-4 py-3 border-t text-sm bg-gray-50/50">
                <button
                    onClick={() => setFiltersFn((p: any) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    disabled={(currentFilters.page ?? 1) <= 1}
                    className="px-3 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                    ← Назад
                </button>
                <span className="text-gray-600">
                    Страница <span className="font-semibold text-gray-900">{currentFilters.page}</span> из{" "}
                    <span className="font-semibold text-gray-900">{data.meta.last_page}</span>
                </span>
                <button
                    onClick={() => setFiltersFn((p: any) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    disabled={(currentFilters.page ?? 1) >= (data.meta.last_page ?? 1)}
                    className="px-3 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                    Вперед →
                </button>
            </div>
        );
    };

    return (
        <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Бонусный фонд</h1>
            </div>

            {/* ─── Global Balance Stats ────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><Wallet className="w-16 h-16" /></div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Wallet className="w-5 h-5" /></div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Призовые (85%)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">${formatAmount(globalStats?.tournament_fund ?? 0)}</p>
                    <p className="text-[11px] text-gray-400 mt-1">Сумма всех призов, выплаченных победителям турниров</p>
                </div>

                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><Wallet className="w-16 h-16" /></div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-purple-50 rounded-xl text-purple-600"><Wallet className="w-5 h-5" /></div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Бонусный фонд (4%)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">${formatAmount(globalStats?.bonus_fund ?? 0)}</p>
                    <p className="text-[11px] text-gray-400 mt-1">Накапливается со всех турниров. Выплачивается победителю годового турнира</p>
                </div>

                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><Wallet className="w-16 h-16" /></div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-green-50 rounded-xl text-green-600"><Wallet className="w-5 h-5" /></div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Пригласителям (5%)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">${formatAmount(globalStats?.inviter_fund ?? 0)}</p>
                    <p className="text-[11px] text-gray-400 mt-1">Прямые выплаты пригласителям от Millennium за каждый вход участника</p>
                </div>

                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><Wallet className="w-16 h-16" /></div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-amber-50 rounded-xl text-amber-600"><Wallet className="w-5 h-5" /></div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Доля компании (6%)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">${formatAmount(globalStats?.company_fund ?? 0)}</p>
                    <p className="text-[11px] text-gray-400 mt-1">Комиссия компании с каждого турнира после вычета бонусов</p>
                </div>
            </div>

            {/* ─── Tabs ─────────────────────────────────────────────────────────── */}
            <div className="flex gap-1 bg-gray-200/50 p-1 rounded-xl w-fit mt-8">
                {(["history", "payouts"] as Tab[]).map((tab) => {
                    const labels: Record<Tab, string> = {
                        history: "Статистика по турнирам",
                        payouts: "Выплаты",
                    };
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                activeTab === tab
                                    ? "bg-white shadow-sm text-blue-600"
                                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                            }`}
                        >
                            {labels[tab]}
                        </button>
                    );
                })}
            </div>

            {/* ─── History Tab Content ────────────────────────────────────────── */}
            {activeTab === "history" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-4">
                    {/* Filters */}
                    <div className="bg-gray-50 border-b border-gray-100 p-4 flex flex-wrap gap-3 items-end">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                <Search className="w-3 h-3" /> ID турнира
                            </label>
                            <input
                                type="text"
                                value={filterDraft.tournament_id}
                                onChange={(e) => setFilterDraft((d) => ({ ...d, tournament_id: e.target.value }))}
                                placeholder="например 1560361"
                                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-40 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-600">Дата от</label>
                            <input
                                type="date"
                                value={filterDraft.date_from}
                                onChange={(e) => setFilterDraft((d) => ({ ...d, date_from: e.target.value }))}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-600">Дата до</label>
                            <input
                                type="date"
                                value={filterDraft.date_to}
                                onChange={(e) => setFilterDraft((d) => ({ ...d, date_to: e.target.value }))}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                            />
                        </div>
                        <button
                            onClick={applyFilters}
                            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            <Filter className="w-3.5 h-3.5" /> Применить
                        </button>
                        {(filters.tournament_id || filters.date_from || filters.date_to) && (
                            <button
                                onClick={resetFilters}
                                className="text-sm text-gray-400 hover:text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Сбросить
                            </button>
                        )}
                    </div>

                    {historyLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr className="text-gray-500 uppercase text-[10px] font-bold tracking-widest">
                                        <th className="px-6 py-3">Турнир / Дата</th>
                                        <th className="px-6 py-3">Взносы участников</th>
                                        <th className="px-6 py-3 text-right">Бонусный (4%)</th>
                                        <th className="px-6 py-3 text-right">Пригласителям (5%)</th>
                                        <th className="px-6 py-3 text-right">Компания (6%)</th>
                                        <th className="px-6 py-3 text-right">Призы выплачено</th>
                                        <th className="px-6 py-3"></th>
                                    </tr>
                                    <tr className="text-[10px] text-gray-400 border-b border-gray-100">
                                        <td className="px-6 pb-2"></td>
                                        <td className="px-6 pb-2">Сумма всех entry от Millennium</td>
                                        <td className="px-6 pb-2 text-right">Из company_cut; накопится за год → выплата победителю</td>
                                        <td className="px-6 pb-2 text-right">Прямые зачисления пригласителям (новый флоу)</td>
                                        <td className="px-6 pb-2 text-right">Нетто-доля компании из каждого company_cut</td>
                                        <td className="px-6 pb-2 text-right">Сумма payout:N выплат победителям</td>
                                        <td className="px-6 pb-2"></td>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 text-gray-700">
                                    {fundsData?.data?.map((f) => {
                                        const prizeSum = Object.values(f.payout_amount ?? {}).reduce((acc: number, p: any) => acc + parseFloat(p.amount), 0);
                                        return (
                                            <tr key={f.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-blue-600">ID: {f.tournament_id}</div>
                                                    <div className="text-gray-400 tabular-nums text-xs">{formatDate(f.created_at)}</div>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-gray-900">${formatAmount(f.tournament_amount)}</td>
                                                <td className="px-6 py-4 text-right text-purple-600 font-medium">
                                                    ${formatAmount(f.bonus_amount)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-green-600 font-medium">
                                                    ${formatAmount(f.inviter_amount)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-amber-600 font-medium">
                                                    ${formatAmount(f.company_amount)}
                                                </td>
                                                <td className="px-6 py-4 text-right font-bold text-blue-700">
                                                    ${formatAmount(prizeSum)}
                                                    {f.payout_amount && Object.keys(f.payout_amount).length > 0 && (
                                                        <div className="text-[10px] font-normal text-gray-400">
                                                            {Object.keys(f.payout_amount).length} выплат
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <button
                                                        onClick={() => { setSelectedFundId(f.id); setIsPayoutModalOpen(true); }}
                                                        className="flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                        Выплата
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {(!fundsData?.data || fundsData.data.length === 0) && (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-12 text-center text-gray-400 italic">
                                                Нет данных по турнирам
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {renderPagination(fundsData, filters, setFilters)}
                </div>
            )}

            {/* ─── Payouts Tab Content ─────────────────────────────────────────── */}
            {activeTab === "payouts" && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-4">
                    {/* Filters Row */}
                    <div className="bg-gray-50 border-b border-gray-100 p-4 flex gap-4 items-end">
                        <div className="flex-1 max-w-xs space-y-1">
                            <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                <Filter className="w-3 h-3" />
                                Пул (Фонд)
                            </label>
                            <select
                                value={payoutFilters.fund_type}
                                onChange={(e) => setPayoutFilters((p) => ({ ...p, fund_type: e.target.value, page: 1 }))}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            >
                                <option value="">Все пулы</option>
                                <option value="tournament">Фонд турнира (85%)</option>
                                <option value="bonus">Бонусный фонд (4%)</option>
                                <option value="inviter">Фонд пригласителя (5%)</option>
                                <option value="company">Доля компании (6%)</option>
                            </select>
                        </div>
                    </div>

                    {payoutsLoading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase text-[10px] font-bold tracking-widest">
                                    <tr>
                                        <th className="px-6 py-4">ID</th>
                                        <th className="px-6 py-4">Пул фонда</th>
                                        <th className="px-6 py-4 text-right">Сумма выплаты</th>
                                        <th className="px-6 py-4">Назначение</th>
                                        <th className="px-6 py-4">Комментарий</th>
                                        <th className="px-6 py-4">Дата</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 text-gray-700">
                                    {payoutsData?.data?.map((p) => (
                                        <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 font-mono text-gray-400">#{p.id}</td>
                                            <td className="px-6 py-4">
                                                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                                                    {p.fund_type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-red-600">
                                                -${formatAmount(p.amount)}
                                            </td>
                                            <td className="px-6 py-4">{p.destination || "-"}</td>
                                            <td className="px-6 py-4 whitespace-pre-wrap">{p.comment || "-"}</td>
                                            <td className="px-6 py-4 text-gray-500 tabular-nums">
                                                {formatDate(p.created_at)}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!payoutsData?.data || payoutsData.data.length === 0) && (
                                        <tr>
                                            <td colSpan={6} className="px-6 py-12 text-center text-gray-400 italic">
                                                Никаких выплат не найдено
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {renderPagination(payoutsData, payoutFilters, setPayoutFilters)}
                </div>
            )}

            {/* ─── Payout Modal ─────────────────────────────────────────────────── */}
            {isPayoutModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
                        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Новая выплата из фонда</h3>
                                <p className="text-xs text-gray-500">Заполните детали выплаты</p>
                            </div>
                            <button
                                onClick={() => { setIsPayoutModalOpen(false); setSelectedFundId(null); }}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-all"
                            >
                                <Plus className="w-6 h-6 rotate-45" />
                            </button>
                        </div>
                        <form onSubmit={handleCreatePayout} className="p-6 space-y-4">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Выберите пул для списания *
                                </label>
                                <select
                                    required
                                    value={payoutForm.fund_type}
                                    onChange={(e) => setPayoutForm((f) => ({ ...f, fund_type: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm font-semibold text-gray-800"
                                >
                                    <option value="tournament">Фонд турнира (85%)</option>
                                    <option value="bonus">Бонусный фонд (4%)</option>
                                    <option value="inviter">Фонд пригласителя (5%)</option>
                                    <option value="company">Доля компании (6%)</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Сумма выплаты ($) *
                                </label>
                                <input
                                    autoFocus
                                    required
                                    type="number"
                                    step="0.00000001"
                                    min="0.00000001"
                                    value={payoutForm.amount}
                                    onChange={(e) => setPayoutForm((f) => ({ ...f, amount: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-semibold text-lg"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Назначение платежа
                                </label>
                                <input
                                    type="text"
                                    value={payoutForm.destination}
                                    onChange={(e) => setPayoutForm((f) => ({ ...f, destination: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm"
                                    placeholder="Например, ID победителя: 123"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Комментарий
                                </label>
                                <textarea
                                    rows={3}
                                    value={payoutForm.comment}
                                    onChange={(e) => setPayoutForm((f) => ({ ...f, comment: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm h-24"
                                    placeholder="Описание цели выплаты..."
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setIsPayoutModalOpen(false); setSelectedFundId(null); }}
                                    className="flex-1 px-4 py-3 text-sm font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                                >
                                    Отменить
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreatingPayout || !payoutForm.amount}
                                    className="flex-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-8 py-3 rounded-xl text-sm font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                                >
                                    {isCreatingPayout ? "Создание..." : "Подтвердить выплату"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ─── User Modal ───────────────────────────────────────────────────── */}
            <UserViewModal
                userId={selectedUserId}
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
            />
        </div>
    );
};

export default GameBonusPage;
